import { toFiniteNumber } from "@/lib/ai/visualization/shared/primitives";
import type { VizSort } from "@/lib/ai/visualization/views/compiled-spec";

function toSortableNumber(value: unknown): number {
  return toFiniteNumber(value) ?? 0;
}

/**
 * ranking 系 row を sort 後の表示順へ整え、必要なら上位 N 件だけへ切り詰める。
 *
 * @remarks
 * `limit` は「取得件数」ではなく「表示件数」の契約として扱う。
 * 先に dataset を切ってしまうと、後段の sort toggle や follow-up evidence が
 * 元データ全体の順位ではなく、切り出し後の部分集合だけを並べ替える挙動になりやすい。
 * そのため、ranking chart / evidence / export はすべて同じ helper で
 * `sort -> slice(limit)` の順序を共有する。
 */
export function sortAndLimitRankingRows<TRow>(args: {
  rows: readonly TRow[];
  sort: VizSort;
  limit: number | null | undefined;
  getValue: (row: TRow) => unknown;
}): TRow[] {
  const sorted =
    args.sort === "none"
      ? [...args.rows]
      : [...args.rows].sort((left, right) => {
          const leftValue = toSortableNumber(args.getValue(left));
          const rightValue = toSortableNumber(args.getValue(right));
          return args.sort === "asc"
            ? leftValue - rightValue
            : rightValue - leftValue;
        });

  if (typeof args.limit !== "number" || !Number.isFinite(args.limit)) {
    return sorted;
  }

  const normalizedLimit = Math.max(1, Math.trunc(args.limit));
  return sorted.slice(0, normalizedLimit);
}

/**
 * 上位 N 件のカード文言と UI 操作が食い違わないよう、ranking sort control の既定表示を決める。
 *
 * @remarks
 * `top 20` / `bottom 10` のように `limit` が固定されたカードでは、
 * 後から sort を反転できると title/description と表示内容がズレやすい。
 * 明示的に無効化されていない限り、件数制約つき ranking では sort control を隠す。
 */
export function shouldAllowRankingSortControl(args: {
  requested: boolean | null | undefined;
  limit: number | null | undefined;
}): boolean {
  if (args.requested === false) {
    return false;
  }
  return args.limit == null;
}
